import {
  clearIndexedDBError,
  clearObjectStore,
  countData,
  deleteDatabase,
  isRetryableWriteError,
  openDatabase,
} from './utils';
import { getProfileIdKey } from './profileId';

// Per-study databases (state, undo history, beat) stay closed until the report page validates the
// profile via a successful report fetch and enables persistence. This blocks every open path —
// direct upserts, the debounced store persistence, and on-demand reads alike — so an invalid or
// stale profile id in the URL never creates a junk database.
let persistenceEnabled = false;

/** Error name used when an open is refused because persistence is not yet enabled (an expected,
 * non-error condition callers can detect without matching message text). */
export const PERSISTENCE_DISABLED = 'PersistenceDisabledError';

/** Whether an error is the expected "persistence not enabled" refusal from a per-study open. */
export const isPersistenceDisabledError = (error: unknown): boolean =>
  (error as { name?: string })?.name === PERSISTENCE_DISABLED;

/** Allow per-study database opens (called once the report fetch validates the profile). */
export const enableReportPersistence = () => {
  persistenceEnabled = true;
};

/** Block per-study database opens (called at the start of each report (re)load until validated). */
export const disableReportPersistence = () => {
  persistenceEnabled = false;
};

/**
 * Creates the shared machinery for a per-study IndexedDB cache (the beat and ECG worker stores).
 * Owns the module-scoped connection handle and names each database `${studyId}_${dbSuffix}`,
 * falling back to the profile id key from the URL. Callers keep their own domain read/write methods
 * and error logging; this only removes the identical open/remove/clear/count/handle boilerplate.
 *
 * @param config - Database name suffix, object store name, and its key path.
 * @returns The per-study database handle accessor plus open/remove/clear/count helpers.
 */
export const createPerStudyDb = (config: {
  dbSuffix: string;
  storeName: string;
  keyPath: string;
}) => {
  const { dbSuffix, storeName, keyPath } = config;
  let handle: IDBDatabase | null = null;
  let openInFlight: Promise<IDBDatabase> | null = null;
  let inFlightName: string | null = null;

  // Resolve the database name at call time — the profile id comes from the URL, which is not known
  // when this module is first imported. Returns null for a missing/invalid id so we never create a
  // junk `undefined_*` / `null_*` database.
  const resolveName = (studyId?: string): string | null => {
    const id = studyId || getProfileIdKey();
    if (!id || id === 'undefined' || id === 'null') return null;
    return `${id}_${dbSuffix}`;
  };

  // Single writer of `handle`: reuses the live connection, coalesces concurrent opens of the same
  // database, and closes a differently-named handle before switching. Both openDb and reopen go
  // through here so an in-flight open cannot overwrite a handle a later reopen just established.
  const connect = (name: string): Promise<IDBDatabase> => {
    if (!persistenceEnabled) {
      const error = new Error(`[${dbSuffix}] persistence not enabled; refusing to open a database`);
      error.name = PERSISTENCE_DISABLED;
      return Promise.reject(error);
    }
    if (handle && handle.name === name) return Promise.resolve(handle);
    if (openInFlight && inFlightName === name) return openInFlight;
    if (handle) {
      try {
        handle.close();
      } catch {
        /* connection already closing */
      }
      handle = null;
    }
    inFlightName = name;
    openInFlight = openDatabase(name, storeName, keyPath)
      .then((db) => {
        handle = db;
        return db;
      })
      .finally(() => {
        openInFlight = null;
        inFlightName = null;
      });
    return openInFlight;
  };

  const openDb = (studyId?: string) => {
    const name = resolveName(studyId);
    if (!name) {
      return Promise.reject(
        new Error(`[${dbSuffix}] Missing profile id; refusing to open a junk database`),
      );
    }
    return connect(name);
  };

  // Closes the stale handle then reconnects through `connect`, which coalesces with any open already
  // in flight for the same database. Used to recover before retrying a failed write.
  const reopen = () => {
    const name = handle?.name ?? resolveName();
    if (!name) {
      return Promise.reject(new Error(`[${dbSuffix}] Missing profile id; cannot reopen database`));
    }
    if (handle) {
      try {
        handle.close();
      } catch {
        /* connection already closing */
      }
      handle = null;
    }
    return connect(name);
  };

  return {
    /** The current open connection, or null before openDb resolves. */
    get db() {
      return handle;
    },
    /**
     * Opens (and caches) the per-study database, creating its object store on first upgrade.
     * Reuses the live connection when it already points at the requested database, coalesces
     * concurrent opens of the same database so no duplicate connection is created, and closes the
     * previous connection when switching databases so it cannot block a later deletion.
     * Throws on failure so the caller can log with its own prefix; the handle is left unchanged.
     *
     * @param studyId - Study naming the database; falls back to the profile id key.
     */
    openDb,
    /**
     * Runs one write operation, reopening the connection and retrying exactly once when it fails
     * with a retryable (transient or stale-handle) error. Opens the database on demand first, so
     * callers need not pre-open. Non-retryable errors (quota, clone, constraint) propagate as-is.
     *
     * @param op - Write built against the live database handle.
     * @returns A promise resolving to the operation's result.
     */
    runWrite: async <T>(op: (db: IDBDatabase) => Promise<T>): Promise<T> => {
      const db = handle ?? (await openDb());
      try {
        const result = await op(db);
        clearIndexedDBError();
        return result;
      } catch (error) {
        if (!isRetryableWriteError(error)) throw error;
        const result = await op(await reopen());
        clearIndexedDBError();
        return result;
      }
    },
    /**
     * Deletes the whole per-study database for one study, falling back to the profile id key.
     *
     * @param studyId - Study whose database is removed; falls back to the profile id key when empty.
     */
    removeDb: (studyId?: string) => {
      const name = resolveName(studyId);
      return name ? deleteDatabase(name) : Promise.resolve();
    },
    /** Empties the object store without deleting the database. */
    clearStore: () => clearObjectStore(handle!, storeName),
    /** Counts the records held in the object store. */
    countStore: () => countData(handle!, storeName),
  };
};
